import { Component, EventEmitter, Input, Output, OnInit, signal, inject } from '@angular/core';
import { Router } from '@angular/router';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { CookieService } from 'ngx-cookie-service';
import { ToastService } from 'angular-toastify';
import { MenuList } from '../../../../../core/models/system-settings/menu-lists.model';
import { GroupMenuOption } from '../../../../../core/models/system-settings/group-menus.model';
import { GroupSubMenuOption } from '../../../../../core/models/system-settings/group-sub-menus.model';
import { ApiSystemSettingsService } from '../../../../../core/services/api/system-settings/api-system-settings.service';
import { MapperMenuListsService } from '../../../../../core/mappers/system-settings/mapper-menu-lists.service';
import { MenuPermissionService } from '../../../../../core/services/menu-permission.service';
import { MapperUserAccessMenuService } from '../../../../../core/mappers/mapper-user-menu.service';
import { ModalAdminActivityHistoryComponent } from '../modal-admin-activity-history/modal-admin-activity-history.component';
import { AdminActivityEntityTypes } from '../../../../../core/models/system-settings/admin-activity-log.model';

@Component({
  selector: 'app-modal-menu-list',
  standalone: true,
  imports: [CommonModule, FormsModule, ModalAdminActivityHistoryComponent],
  templateUrl: './modal-menu-list.component.html',
  styleUrl: './modal-menu-list.component.css'
})
export class ModalMenuListComponent implements OnInit {
  @Input() data: MenuList | null = null;
  @Input() presetContext: {
    groupMenuId: number;
    groupMenuName: string;
    groupSubId: number;
    groupSubName: string;
  } | null = null;
  @Output() close = new EventEmitter<void>();
  @Output() saveSuccess = new EventEmitter<void>();

  readonly activityEntityTypes = AdminActivityEntityTypes;
  showActivityHistory = false;

  formData: Partial<MenuList> = {
    id: 0,
    name: '',
    status: true,
    path: '',
    pathId: null,
    number: 1,
    groupMenuId: 0,
    groupMenuName: '',
    groupSubId: null,
    groupSubName: null
  };

  groupMenuOptions = signal<GroupMenuOption[]>([]);
  groupSubMenuOptions = signal<GroupSubMenuOption[]>([]);
  pathOptions = signal<{ pathId: number; path: string }[]>([]);
  isLoading = signal<boolean>(false);
  openDropdown: string | null = null;

  // Search autocomplete fields (Rule 4)
  searchGroupMenuText: string = '';
  searchSubMenuText: string = '';
  searchPathText: string = '';

  // ─── Dependencies ───
  private toast = inject(ToastService);
  private apiService = inject(ApiSystemSettingsService);
  private mapper = inject(MapperMenuListsService);
  private userMenuMapper = inject(MapperUserAccessMenuService);
  private cookieService = inject(CookieService);
  private router = inject(Router);
  private permissionService = inject(MenuPermissionService);

  // ================= Normal Functions =================

  constructor() { }

  ngOnInit() {
    this.getGroupMenuOptions();
    this.pathOptions.set(this.userMenuMapper.getMenuPathOptions());
    if (this.data) {
      this.formData = { ...this.data };
      this.searchGroupMenuText = this.formData.groupMenuName || '';
      this.searchSubMenuText = this.formData.groupSubName || '';
      this.searchPathText = this.formData.path || '';
      this.getGroupSubMenuOptions(this.formData.groupMenuId);
    } else if (this.presetContext) {
      this.formData.groupMenuId = this.presetContext.groupMenuId;
      this.formData.groupMenuName = this.presetContext.groupMenuName;
      this.formData.groupSubId = this.presetContext.groupSubId;
      this.formData.groupSubName = this.presetContext.groupSubName;
      this.formData.pathId = null;
      this.formData.path = '';
      this.searchGroupMenuText = this.presetContext.groupMenuName;
      this.searchSubMenuText = this.presetContext.groupSubName;
      this.searchPathText = '';
      this.getGroupSubMenuOptions(this.presetContext.groupMenuId);
    } else {
      this.formData.pathId = null;
      this.formData.path = '';
      this.searchPathText = '';
      this.getGroupSubMenuOptions();
    }
  }

  get isContextLocked(): boolean {
    return this.presetContext !== null && !this.data?.id;
  }

  /**
   * Safe close handler that checks input text and validates selection.
   */
  closeDropdowns(): void {
    if (this.openDropdown === 'groupMenu') {
      const options = this.groupMenuOptions() || [];
      const match = options.find(
        (o) => o?.name?.toLowerCase() === this.searchGroupMenuText?.trim()?.toLowerCase()
      );

      if (match) {
        if (this.formData.groupMenuId !== match.id) {
          // If group changes, reset submenu group
          this.formData.groupMenuId = match.id;
          this.formData.groupMenuName = match.name;
          this.formData.groupSubId = null;
          this.formData.groupSubName = null;
          this.searchSubMenuText = '';
          this.getGroupSubMenuOptions(match.id);
        }
        this.searchGroupMenuText = match.name;
      } else {
        // Revert to previously saved value
        this.searchGroupMenuText = this.formData.groupMenuName || '';
      }
    }

    if (this.openDropdown === 'subMenu') {
      const options = this.groupSubMenuOptions() || [];
      const match = options.find(
        (o) => o?.name?.toLowerCase() === this.searchSubMenuText?.trim()?.toLowerCase()
      );

      if (match) {
        this.formData.groupSubId = match.id;
        this.formData.groupSubName = match.name;
        this.searchSubMenuText = match.name;
      } else {
        // Revert to previously saved value
        this.searchSubMenuText = this.formData.groupSubName || '';
      }
    }

    if (this.openDropdown === 'path') {
      const options = this.pathOptions() || [];
      const match = options.find(
        (o) => o?.path?.toLowerCase() === this.searchPathText?.trim()?.toLowerCase()
      );

      if (match) {
        this.formData.pathId = match.pathId;
        this.formData.path = match.path;
        this.searchPathText = match.path;
      } else {
        // Revert to previously saved value
        this.searchPathText = this.formData.path || '';
      }
    }
    this.openDropdown = null;
  }

  /**
   * Filters the path options by text search.
   */
  get filteredPathOptions(): { pathId: number; path: string }[] {
    const query = (this.searchPathText || '').toLowerCase().trim();
    const options = this.pathOptions() || [];
    if (!query) return options;
    return options.filter((o) =>
      o?.path?.toLowerCase().includes(query) ||
      String(o?.pathId ?? '').includes(query)
    );
  }

  /**
   * Selects path option.
   * @param opt Selected path option
   */
  selectPath(opt: { pathId: number; path: string }): void {
    this.formData.pathId = opt.pathId;
    this.formData.path = opt.path;
    this.searchPathText = opt.path;
    this.openDropdown = null;
  }

  /**
   * Filters the parent options by text search.
   */
  get filteredGroupMenuOptions(): GroupMenuOption[] {
    const query = (this.searchGroupMenuText || '').toLowerCase().trim();
    const options = this.groupMenuOptions() || [];
    if (!query) return options;
    return options.filter(o => o?.name?.toLowerCase().includes(query));
  }

  /**
   * Filters the sub-parent options by text search.
   */
  get filteredGroupSubMenuOptions(): GroupSubMenuOption[] {
    const query = (this.searchSubMenuText || '').toLowerCase().trim();
    const options = this.groupSubMenuOptions() || [];
    if (!query) return options;
    return options.filter(o => o?.name?.toLowerCase().includes(query));
  }

  /**
   * Opens the targeted dropdown.
   * @param menu Dropdown identifier
   */
  onInputFocus(menu: string): void {
    if (this.openDropdown && this.openDropdown !== menu) {
      this.closeDropdowns();
    }
    this.openDropdown = menu;
  }

  /**
   * Handles click inside the overlay.
   * @param event The mouse click event
   */
  onClickInsideModal(event: Event): void {
    event.stopPropagation();
    this.closeDropdowns();
  }

  /**
   * Toggles dropdown menu.
   * @param dropdown Selected dropdown identifier
   * @param event The click event
   */
  toggleDropdown(dropdown: string, event: Event): void {
    event.stopPropagation();
    if (this.openDropdown === dropdown) {
      this.closeDropdowns();
    } else {
      if (this.openDropdown) this.closeDropdowns();
      this.openDropdown = dropdown;
    }
  }

  /**
   * Selects group menu option.
   * @param opt Selected group menu option
   */
  selectGroupMenu(opt: GroupMenuOption): void {
    this.formData.groupMenuId = opt.id;
    this.formData.groupMenuName = opt.name;
    this.searchGroupMenuText = opt.name;
    this.openDropdown = null;

    // Reset submenu
    this.formData.groupSubId = null;
    this.formData.groupSubName = null;
    this.searchSubMenuText = '';

    // Reload submenu options
    this.getGroupSubMenuOptions(opt.id);
  }

  /**
   * Selects group submenu option.
   * @param opt Selected group submenu option
   */
  selectSubMenu(opt: GroupSubMenuOption): void {
    this.formData.groupSubId = opt.id;
    this.formData.groupSubName = opt.name;
    this.searchSubMenuText = opt.name;
    this.openDropdown = null;
  }

  onClose() {
    this.close.emit();
  }

  /**
   * Validates and updates/creates the menu list item.
   */
  onSave() {
    this.closeDropdowns();

    if (!this.formData.name || !this.formData.groupMenuId || this.formData.number === undefined) {
      this.toast.error('กรุณากรอกข้อมูลให้ครบถ้วนและเลือกหมวดหมู่ให้ถูกต้อง');
      return;
    }

    if (!this.formData.groupSubId) {
      this.toast.error('กรุณาเลือกกลุ่มเมนูย่อย');
      return;
    }

    if (!this.formData.pathId || !this.formData.path) {
      this.toast.error('กรุณาเลือกเส้นทาง (PATH) ให้ถูกต้อง');
      return;
    }

    this.isLoading.set(true);

    // Extract user ID from cookie
    const userIdStr = this.cookieService.get('user_id');
    const userId = userIdStr ? parseInt(userIdStr, 10) : 0;

    // Get menuId from the current route
    const currentPath = this.router.url;
    const menuId = this.permissionService.getMenuId(currentPath) || 15; // Fallback to 15 if not found

    const payload = this.mapper.mapMenuListToPayload(this.formData as MenuList, userId, menuId);

    if (this.formData.id) {
      this.apiService.updateMenuList(payload, this.formData.id).subscribe({
        next: (res: any) => {
          console.log("update data : ", res, JSON.stringify(res));
          if (res?.isSuccess) {
            this.toast.success('แก้ไขข้อมูลสำเร็จ');
            this.saveSuccess.emit();
            this.close.emit();
          } else {
            this.toast.error(res?.message || 'แก้ไขข้อมูลไม่สำเร็จ');
          }
          this.isLoading.set(false);
        },
        error: (err: any) => {
          console.error(err);
          this.toast.error('เกิดข้อผิดพลาดในการเชื่อมต่อเซิร์ฟเวอร์');
          this.isLoading.set(false);
        }
      });
    } else {
      this.apiService.createMenuList(payload).subscribe({
        next: (res: any) => {
          console.log("create data : ", res, JSON.stringify(res));
          if (res?.isSuccess) {
            this.toast.success('สร้างข้อมูลสำเร็จ');
            this.saveSuccess.emit();
            this.close.emit();
          } else {
            this.toast.error(res?.message || 'สร้างข้อมูลไม่สำเร็จ');
          }
          this.isLoading.set(false);
        },
        error: (err: any) => {
          console.error(err);
          this.toast.error('เกิดข้อผิดพลาดในการเชื่อมต่อเซิร์ฟเวอร์');
          this.isLoading.set(false);
        }
      });
    }
  }

  // ================= API Functions =================

  /**
   * Loads options for parent select dropdown.
   */
  getGroupMenuOptions(): void {
    this.apiService.getGroupMenuOptions().subscribe({
      next: (options: GroupMenuOption[]) => {
        this.groupMenuOptions.set(options || []);
      },
      error: () => {
        const fallbackOptions = [
          { id: 1, name: 'HOME' },
          { id: 2, name: 'PARKING SYSTEM' },
          { id: 3, name: 'SETTINGS' },
          { id: 4, name: 'อื่นๆ' }
        ];
        this.groupMenuOptions.set(fallbackOptions);
      }
    });
  }

  /**
   * Loads options for sub-parent select dropdown, optionally filtered.
   * @param groupMenuId Optional parent group ID filter
   */
  getGroupSubMenuOptions(groupMenuId?: number): void {
    this.apiService.getGroupSubMenuOptions(groupMenuId).subscribe({
      next: (options: GroupSubMenuOption[]) => {
        this.groupSubMenuOptions.set(options || []);
      },
      error: () => {
        const fallbackOptions = [
          { id: 101, name: 'จัดการข้อมูลลูกค้า' },
          { id: 102, name: 'จัดการที่จอดรถ' },
          { id: 103, name: 'การเงิน' },
          { id: 104, name: 'จัดการเมนู' }
        ];
        this.groupSubMenuOptions.set(fallbackOptions);
      }
    });
  }

  get canShowActivityHistory(): boolean {
    return !!this.formData.id && this.formData.id > 0;
  }

  openActivityHistory(): void {
    if (!this.canShowActivityHistory) return;
    this.showActivityHistory = true;
  }

  closeActivityHistory(): void {
    this.showActivityHistory = false;
  }
}
